import { useState } from "react";
import Add from "@mui/icons-material/Add";
import Cancel from "@mui/icons-material/Cancel";
import Edit from "@mui/icons-material/Edit";
import Save from "@mui/icons-material/Save";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { Section12Language } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import {
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export function Section12LanguagesManagementPage() {
  const [{ data: languages, loading }, refetchLanguages] =
    apiHooks.section12.getAllLanguages();

  const [newLanguageName, setNewLanguageName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleCreate = async () => {
    if (!newLanguageName.trim()) {
      return;
    }
    try {
      await api.section12.createLanguage({
        languageName: newLanguageName.trim(),
      });
      setNewLanguageName("");
      renderSuccessToast({ message: "Language created successfully" });
      void refetchLanguages();
    } catch (error) {
      console.error("Failed to create language:", error);
      renderErrorToast({ message: "Failed to create language" });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editingName.trim()) {
      return;
    }
    try {
      await api.section12.updateLanguage(id, {
        languageName: editingName.trim(),
      });
      setEditingId(null);
      setEditingName("");
      renderSuccessToast({ message: "Language updated successfully" });
      void refetchLanguages();
    } catch (error) {
      console.error("Failed to update language:", error);
      renderErrorToast({ message: "Failed to update language" });
    }
  };

  const startEditing = (language: Section12Language) => {
    setEditingId(language.id);
    setEditingName(language.languageName);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <LoadingSpinner label="Loading languages..." />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Section 12 Languages
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Manage the list of languages available for Section 12 doctors to select.
      </Typography>
      {/* Add new language */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginBottom: "1em",
          gap: 2,
        }}
      >
        <TextField
          sx={{ flexGrow: 1 }}
          value={newLanguageName}
          onChange={(e) => {
            setNewLanguageName(e.target.value);
          }}
          placeholder="Enter new language name"
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void handleCreate();
            }
          }}
        />
        <IconButton
          color="primary"
          disabled={!newLanguageName.trim()}
          onClick={() => {
            void handleCreate();
          }}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "grey.300" },
          }}
        >
          <Add />
        </IconButton>
      </Box>
      {/* Languages table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="languages table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Language Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 150 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!languages || languages.length === 0) && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                      py: 2,
                    }}
                  >
                    No languages defined yet. Add one above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {languages?.map((language) => (
              <TableRow
                key={language.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {editingId === language.id ? (
                    <TextField
                      value={editingName}
                      onChange={(e) => {
                        setEditingName(e.target.value);
                      }}
                      size="small"
                      fullWidth
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          void handleUpdate(language.id);
                        } else if (e.key === "Escape") {
                          cancelEditing();
                        }
                      }}
                    />
                  ) : (
                    language.languageName
                  )}
                </TableCell>
                <TableCell>
                  {editingId === language.id ? (
                    <>
                      <IconButton
                        onClick={() => {
                          void handleUpdate(language.id);
                        }}
                        color="primary"
                        title="Save"
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        onClick={cancelEditing}
                        color="default"
                        title="Cancel"
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      onClick={() => {
                        startEditing(language);
                      }}
                      title="Edit"
                    >
                      <Edit />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
